import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { playTextToSpeech } from '../services/voiceCommandService';
import { useI18n } from '../i18n';

const AudioContext = createContext(null);

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const { language, t } = useI18n();
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSpeechPaused, setIsSpeechPaused] = useState(false);
  const [speechLabel, setSpeechLabel] = useState(t('audio.agentReading'));
  const audioRef = useRef(new Audio());
  const originalVolumeRef = useRef(1);
  const activeUtteranceRef = useRef(null);
  const ttsAudioRef = useRef(new Audio());
  const speechRunIdRef = useRef(0);
  const duckedAudioRef = useRef(false);

  const restoreDuckedAudio = useCallback(() => {
    if (duckedAudioRef.current) {
      audioRef.current.volume = originalVolumeRef.current;
      duckedAudioRef.current = false;
    }
  }, []);

  const playFile = useCallback((url) => {
    const audio = audioRef.current;
    if (audio.src !== url) {
      audio.src = url;
    }
    audio.volume = volume;
    window.dispatchEvent(new Event('q-speech-suspend'));
    audio.play().then(() => {
      setIsPlaying(true);
    }).catch(error => {
      console.error("Error playing audio file:", error);
      window.dispatchEvent(new Event('q-speech-resume'));
    });

    audio.onended = () => {
      setIsPlaying(false);
      window.dispatchEvent(new Event('q-speech-resume'));
    };
  }, [volume]);

  const speak = useCallback(async (text, options = {}) => {
    const cleanText = String(text || '').replace(/\s+/g, ' ').trim();
    if (!cleanText) return;

    const runId = Date.now();
    speechRunIdRef.current = runId;
    window.speechSynthesis?.cancel();
    restoreDuckedAudio();

    const audio = audioRef.current;
    const wasPlaying = isPlaying && !audio.paused;

    if (wasPlaying) {
      originalVolumeRef.current = audio.volume;
      audio.volume = 0.2; // Audio ducking
      duckedAudioRef.current = true;
    }

    setSpeechLabel(options.label || t('audio.agentReading'));
    setIsSpeaking(true);
    setIsSpeechPaused(false);
    window.dispatchEvent(new Event('q-speech-suspend'));
    window.dispatchEvent(new CustomEvent('q-agent-thinking', { detail: false }));

    try {
      await playTextToSpeech(cleanText, {
        ...options,
        lang: options.lang || language,
        audioElement: ttsAudioRef.current,
        onStart: options.onStart,
        onError: options.onError,
        onEnd: () => {
          options.onEnd?.();
        },
      });
    } catch (event) {
      if (event?.error !== 'interrupted' && event?.error !== 'canceled') {
        console.error('AudioContext.speak error', event);
      }
    } finally {
      if (speechRunIdRef.current === runId) {
        window.dispatchEvent(new Event('q-speech-resume'));
        restoreDuckedAudio();
        activeUtteranceRef.current = null;
        setIsSpeaking(false);
        setIsSpeechPaused(false);
      }
    }
  }, [isPlaying, restoreDuckedAudio, language, t]);

  const pauseSpeech = useCallback(() => {
    if (!ttsAudioRef.current.paused) {
      ttsAudioRef.current.pause();
      setIsSpeechPaused(true);
      return;
    }
    if (window.speechSynthesis?.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsSpeechPaused(true);
    }
  }, []);

  const resumeSpeech = useCallback(() => {
    if (ttsAudioRef.current.paused && ttsAudioRef.current.src) {
      ttsAudioRef.current.play().then(() => setIsSpeechPaused(false)).catch(error => console.error('Error resuming TTS audio:', error));
      return;
    }
    if (window.speechSynthesis?.paused) {
      window.speechSynthesis.resume();
      setIsSpeechPaused(false);
    }
  }, []);

  const toggleSpeech = useCallback(() => {
    if (window.speechSynthesis?.paused || isSpeechPaused) {
      resumeSpeech();
    } else {
      pauseSpeech();
    }
  }, [isSpeechPaused, pauseSpeech, resumeSpeech]);

  const stopAll = useCallback((options = {}) => {
    const { resumeSpeech = true } = options;
    // Stop MP3 playback
    const audio = audioRef.current;
    const hadActiveAudio = isPlaying || isSpeaking || !audio.paused || !ttsAudioRef.current.paused || window.speechSynthesis?.speaking || window.speechSynthesis?.pending;
    if (!audio.paused) {
      audio.pause();
      audio.currentTime = 0; // Optional: Reset to start
    }
    setIsPlaying(false);

    // Stop TTS playback
    const ttsAudio = ttsAudioRef.current;
    if (!ttsAudio.paused) {
      ttsAudio.pause();
      ttsAudio.currentTime = 0;
    }
    speechRunIdRef.current = Date.now();
    if (window.speechSynthesis?.speaking || window.speechSynthesis?.pending) {
      window.speechSynthesis.cancel();
    }
    activeUtteranceRef.current = null;
    restoreDuckedAudio();
    setIsSpeaking(false);
    setIsSpeechPaused(false);
    if (hadActiveAudio && resumeSpeech) {
      window.dispatchEvent(new Event('q-speech-resume'));
    }
  }, [isPlaying, isSpeaking, restoreDuckedAudio]);

  const value = {
    isPlaying,
    volume,
    setVolume,
    isSpeaking,
    isSpeechPaused,
    speechLabel,
    playFile,
    speak,
    pauseSpeech,
    resumeSpeech,
    toggleSpeech,
    stopAll,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
